.page {
  min-height: 100vh;
  /* <main> is a flex column scroller: without this the page shrinks to one
     screen, its content overflows it, and the sticky bar lets go at the fold. */
  flex-shrink: 0;
  background: var(--bg-base);
}

/* Loading / Error */
.loading,
.error {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 50vh;
  gap: var(--space-4);
  color: var(--text-muted);
}

.backBtn {
  padding: var(--space-2) var(--space-4);
  background: var(--bg-card);
  border: 1px solid var(--border-default);
  border-radius: var(--radius-md);
  color: var(--text-secondary);
  cursor: pointer;
}

/* Hero Banner */
/* ── Sticky compact header ─────────────────────────────────
   Zero-height sticky anchor: takes no space in the flow, sticks for the whole
   page, and the bar inside hangs from it. top offsets <main>'s 24px padding
   so the bar sits flush under the app header. */
.stickyAnchor {
  position: sticky;
  top: calc(-1 * var(--space-6));
  z-index: var(--z-sticky);
  height: 0;
  margin: 0 calc(-1 * var(--space-8));
}

.stickyBar {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  height: 64px;
  padding: 0 var(--space-8);
  background: color-mix(in srgb, var(--bg-base) 92%, transparent);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border-bottom: 1px solid var(--border-subtle);
  box-shadow: var(--shadow-md);
  opacity: 0;
  translate: 0 -8px;
  pointer-events: none;
  transition:
    opacity var(--duration-fast) var(--ease-default),
    translate var(--duration-fast) var(--ease-default);
}

.stickyBar[data-visible] {
  opacity: 1;
  translate: 0 0;
  pointer-events: auto;
}

.stickyPoster {
  width: 32px;
  height: 46px;
  border-radius: var(--radius-sm);
  object-fit: cover;
  background: var(--bg-elevated);
  flex-shrink: 0;
}

.stickyTitle {
  font-size: var(--text-xl);
  font-weight: var(--weight-bold);
  color: var(--text-primary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
}

.stickyMeta {
  font-size: var(--text-sm);
  color: var(--text-muted);
  white-space: nowrap;
}

@media (prefers-reduced-motion: reduce) {
  .stickyBar {
    transition: none;
  }
}

.hero {
  position: relative;
  min-height: 400px;
  overflow: hidden;
  /* Bleed to the edges of <main>, whose padding is 24px top / 32px sides */
  margin: calc(-1 * var(--space-6)) calc(-1 * var(--space-8)) 0;
}

.heroBg {
  position: absolute;
  inset: 0;
  background-size: cover;
  background-position: center top;
  opacity: 0.3;
  filter: saturate(0.5);
}

.heroGradient {
  position: absolute;
  inset: 0;
  /* From the page colour, so it fades into the page in either theme
     (dark --bg-base is the #08090c this used to hard-code). */
  background: linear-gradient(
    to top,
    var(--bg-base) 0%,
    color-mix(in srgb, var(--bg-base) 80%, transparent) 40%,
    color-mix(in srgb, var(--bg-base) 40%, transparent) 100%
  );
}

/* Light: the backdrop as a faint watermark that fades out, not a grey wash */
:global([data-theme='light']) .heroBg {
  opacity: 0.14;
  filter: grayscale(100%);
  mix-blend-mode: luminosity;
  mask-image: linear-gradient(to bottom, #000 35%, transparent 92%);
  -webkit-mask-image: linear-gradient(to bottom, #000 35%, transparent 92%);
}

.heroContent {
  position: relative;
  z-index: 1;
  height: 100%;
  display: flex;
  flex-direction: column;
  padding: var(--space-6) var(--space-8);
}

.backButton {
  display: inline-flex;
  align-items: center;
  gap: var(--space-1);
  padding: var(--space-1) var(--space-2);
  background: transparent;
  border: none;
  color: var(--text-muted);
  font-size: var(--text-sm);
  cursor: pointer;
  margin-bottom: var(--space-4);
  width: fit-content;
}

.backButton:hover {
  color: var(--text-primary);
}

.heroMain {
  flex: 1;
  display: flex;
  align-items: flex-end;
  gap: var(--space-6);
}

.heroPoster {
  width: 200px;
  height: 300px;
  border-radius: var(--radius-lg);
  object-fit: cover;
  box-shadow: 0 16px 48px rgba(0, 0, 0, 0.7);
  border: 1px solid var(--border-subtle);
  flex-shrink: 0;
}

.heroInfo {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
  min-width: 0;
  padding-bottom: var(--space-2);
}

.heroTitleRow {
  display: flex;
  align-items: center;
  gap: var(--space-4);
  flex-wrap: wrap;
}

/* All-time revenue rank — Bebas figure, as the rank column in Revenue & Rank */
.heroRank {
  display: inline-flex;
  align-items: baseline;
  gap: 2px;
  padding: var(--space-1) var(--space-3);
  border: 1px solid color-mix(in srgb, var(--accent-secondary) 45%, transparent);
  border-radius: var(--radius-md);
  background: color-mix(in srgb, var(--accent-secondary) 10%, transparent);
  font-family: var(--font-display);
  font-size: 28px;
  line-height: 1;
  color: var(--text-primary);
  cursor: help;
}

.heroRankPound {
  color: var(--accent-secondary-text);
}

.heroRankLabel {
  margin-left: var(--space-2);
  font-family: var(--font-sans);
  font-size: var(--text-xs);
  font-weight: var(--weight-semi);
  letter-spacing: var(--tracking-caps);
  text-transform: uppercase;
  color: var(--text-secondary);
}

/* Fact lines: items divided by thin vertical rules (d360) */
.heroFacts {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  row-gap: var(--space-2);
  font-size: var(--text-md);
  color: var(--text-secondary);
}

.heroFacts > * {
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
  padding: 0 var(--space-4);
  border-left: 1px solid var(--border-default);
}

.heroFacts > *:first-child {
  padding-left: 0;
  border-left: 0;
}

.heroGenres {
  color: var(--accent-secondary-text);
}

.heroFact {
  gap: var(--space-3);
}

.heroFactIcon {
  color: var(--text-muted);
}

.heroPlatform {
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
  white-space: nowrap;
}

.heroFlag {
  font-size: 18px;
  line-height: 1;
}

.heroMore {
  color: var(--text-muted);
  font-weight: var(--weight-semi);
}

.heroDescription {
  margin: 0;
  max-width: 90ch;
  font-size: var(--text-base);
  line-height: 1.55;
  color: var(--text-secondary);
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.heroActions {
  display: flex;
  gap: var(--space-2);
  margin-top: var(--space-1);
}

.heroAction {
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
  height: 34px;
  padding: 0 var(--space-3);
  border: 1px solid var(--border-default);
  border-radius: var(--radius-md);
  background: color-mix(in srgb, var(--bg-elevated) 80%, transparent);
  font-size: var(--text-sm);
  font-weight: var(--weight-semi);
  color: var(--text-primary);
  text-decoration: none;
  transition: border-color var(--duration-fast) var(--ease-default);
}

.heroAction:hover {
  border-color: var(--accent-secondary);
}

.heroTitle {
  font-size: var(--text-4xl, var(--text-3xl));
  font-weight: var(--weight-bold);
  color: var(--text-primary);
  margin: 0;
  line-height: 1.2;
  letter-spacing: var(--tracking-tight);
}




/* KPI Grid */
.kpiGrid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: var(--space-4);
  padding: var(--space-6);
  padding-top: var(--space-8);
}

@media (max-width: 1400px) {
  .kpiGrid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 760px) {
  .kpiGrid {
    grid-template-columns: minmax(0, 1fr);
  }
}

/* Emphasised first line inside a StatWidget aside (e.g. the top region) */
/* Streaming-economics charts (d360 parity) stack vertically, full width. */
.charts {
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
  padding: 0 var(--space-6) var(--space-6);
}

.chartsError {
  margin: 0 var(--space-6) var(--space-6);
  padding: var(--space-6);
  background: var(--bg-card);
  border: 1px solid var(--border-subtle);
  border-radius: var(--radius-lg);
  text-align: center;
  color: var(--text-muted);
  font-size: var(--text-sm);
}

/* Skeleton chart cards (loading state) */
.skeletonCard {
  background: var(--bg-card);
  border: 1px solid var(--border-subtle);
  border-radius: var(--radius-lg);
  padding: var(--space-5);
}

.skeletonCardHeader {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: var(--space-4);
  margin-bottom: var(--space-4);
  flex-wrap: wrap;
}

/* Season Breakdown Section */
.seasonSection {
  margin: 0 var(--space-6) var(--space-6);
  background: var(--bg-card);
  border: 1px solid var(--border-subtle);
  border-radius: var(--radius-lg);
  padding: var(--space-5);
}

.sectionHeader {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  margin-bottom: var(--space-3);
  color: var(--text-muted);
}

.sectionTitle {
  font-size: var(--text-lg);
  font-weight: var(--weight-semi);
  color: var(--text-primary);
  margin: 0;
}

.narrativeInsight {
  font-size: var(--text-sm);
  color: var(--text-secondary);
  margin: 0 0 var(--space-4);
  padding: var(--space-3);
  background: var(--bg-surface);
  border-radius: var(--radius-md);
  border-left: 3px solid var(--accent-primary);
}

.seasonTableWrapper {
  overflow-x: auto;
}

.seasonTable {
  width: 100%;
  border-collapse: collapse;
  font-size: var(--text-sm);
}

.seasonTable th {
  text-align: left;
  padding: var(--space-3) var(--space-4);
  font-size: var(--text-xs);
  font-weight: var(--weight-semi);
  color: var(--text-muted);
  text-transform: uppercase;
  letter-spacing: var(--tracking-caps);
  border-bottom: 1px solid var(--border-default);
  white-space: nowrap;
}

.seasonTable td {
  padding: var(--space-3) var(--space-4);
  border-bottom: 1px solid var(--border-subtle);
  color: var(--text-secondary);
}

.seasonTable tr:last-child td {
  border-bottom: none;
}

.seasonNum {
  font-weight: var(--weight-semi);
  color: var(--text-primary);
}

.releaseBadge {
  display: inline-block;
  padding: 2px var(--space-2);
  border-radius: var(--radius-sm);
  font-size: var(--text-xs);
  font-weight: var(--weight-medium);
}

.releaseBadge.weekly {
  background: var(--accent-secondary-muted);
  color: var(--accent-secondary-text);
}

.releaseBadge.binge {
  background: var(--accent-primary-muted);
  color: var(--accent-primary);
}

.revenueCell {
  font-family: var(--font-display);
  font-weight: var(--weight-bold);
  color: var(--text-primary);
}

.perEpisodeCell {
  color: var(--text-secondary);
}

.rankCell {
  font-weight: var(--weight-semi);
  color: var(--accent-secondary-text);
}
